import { chordFrequencies } from '../audio/celestialPadEngine';
import type { ChordAccompaniment, ChordId, NoteId } from '../types';

export const MAX_CHORD_NOTES = 6;

const SOLFEGE = ['Do', 'Do#', 'Re', 'Re#', 'Mi', 'Fa', 'Fa#', 'Sol', 'Sol#', 'La', 'La#', 'Si'] as const;

/** Notas de Do Mayor para añadir (octavas 2–5). */
export const ADD_NOTE_OPTIONS: { note: NoteId; octave: number; freq: number; color: string }[] = [
  { note: 'do', octave: 3, freq: 130.81, color: '#E53935' },
  { note: 're', octave: 3, freq: 146.83, color: '#FB8C00' },
  { note: 'mi', octave: 3, freq: 164.81, color: '#FDD835' },
  { note: 'fa', octave: 3, freq: 174.61, color: '#43A047' },
  { note: 'sol', octave: 3, freq: 196.0, color: '#29B6F6' },
  { note: 'la', octave: 3, freq: 220.0, color: '#7E57C2' },
  { note: 'si', octave: 3, freq: 246.94, color: '#EC407A' },
  { note: 'do', octave: 4, freq: 261.63, color: '#E53935' },
  { note: 're', octave: 4, freq: 293.66, color: '#FB8C00' },
  { note: 'mi', octave: 4, freq: 329.63, color: '#FDD835' },
  { note: 'fa', octave: 4, freq: 349.23, color: '#43A047' },
  { note: 'sol', octave: 4, freq: 392.0, color: '#29B6F6' },
  { note: 'la', octave: 4, freq: 440.0, color: '#7E57C2' },
  { note: 'si', octave: 4, freq: 493.88, color: '#EC407A' },
  { note: 'do', octave: 5, freq: 523.25, color: '#E53935' },
  { note: 're', octave: 5, freq: 587.33, color: '#FB8C00' },
  { note: 'mi', octave: 5, freq: 659.25, color: '#FDD835' },
  { note: 'fa', octave: 5, freq: 698.46, color: '#43A047' },
  { note: 'sol', octave: 5, freq: 783.99, color: '#29B6F6' },
  { note: 'la', octave: 5, freq: 880.0, color: '#7E57C2' },
  { note: 'si', octave: 5, freq: 987.77, color: '#EC407A' },
];

const NOTE_LABELS: Record<NoteId, string> = {
  do: 'Do',
  re: 'Re',
  mi: 'Mi',
  fa: 'Fa',
  sol: 'Sol',
  la: 'La',
  si: 'Si',
  rest: '—',
};

export function defaultVoicing(chordId: ChordId): number[] {
  return [...chordFrequencies(chordId)];
}

export function resolveVoicing(
  acc: ChordAccompaniment | null | undefined,
  chordId: ChordId,
): number[] {
  const custom = acc?.voicings?.[chordId];
  if (custom && custom.length > 0) return [...custom];
  return defaultVoicing(chordId);
}

export function hasCustomVoicing(
  acc: ChordAccompaniment | null | undefined,
  chordId: ChordId,
): boolean {
  const custom = acc?.voicings?.[chordId];
  return Boolean(custom && custom.length > 0);
}

export function freqToSolfegeLabel(freq: number): string {
  const midi = Math.round(12 * Math.log2(freq / 440) + 69);
  const octave = Math.floor(midi / 12) - 1;
  const name = SOLFEGE[((midi % 12) + 12) % 12];
  return `${name}${octave}`;
}

export function shiftNoteOctave(freq: number, octaves: number): number {
  if (octaves === 0) return freq;
  return freq * 2 ** octaves;
}

export function shiftVoicingOctave(notes: number[], octaves: number): number[] {
  return notes.map((f) => shiftNoteOctave(f, octaves));
}

export function removeNoteAt(notes: number[], index: number): number[] {
  if (notes.length <= 1) return notes;
  return notes.filter((_, i) => i !== index);
}

export function addNoteToVoicing(notes: number[], freq: number): number[] {
  if (notes.length >= MAX_CHORD_NOTES) return notes;
  if (notes.some((f) => Math.abs(f - freq) < 0.5)) return notes;
  return [...notes, freq].sort((a, b) => a - b);
}

export function setChordVoicing(
  acc: ChordAccompaniment,
  chordId: ChordId,
  notes: number[],
): ChordAccompaniment {
  const voicings = { ...acc.voicings, [chordId]: [...notes] };
  return { ...acc, voicings };
}

export function resetChordVoicing(acc: ChordAccompaniment, chordId: ChordId): ChordAccompaniment {
  if (!acc.voicings?.[chordId]) return acc;
  const voicings = { ...acc.voicings };
  delete voicings[chordId];
  return { ...acc, voicings };
}

export function cloneVoicings(
  voicings: ChordAccompaniment['voicings'],
): ChordAccompaniment['voicings'] {
  if (!voicings) return {};
  return Object.fromEntries(
    Object.entries(voicings).map(([id, freqs]) => [id, [...freqs]]),
  ) as Partial<Record<ChordId, number[]>>;
}

export function formatAddOptionLabel(note: NoteId, octave: number): string {
  return `${NOTE_LABELS[note]}${octave}`;
}
